import React, { ReactElement } from 'react';
import {
  Grid,
  GridCellProps,
  GridColumn,
  GridDataStateChangeEvent,
  GridCustomFilterCellProps,
} from '@progress/kendo-react-grid';
import { process, State } from '@progress/kendo-data-query';

import { useSelector } from 'react-redux';
import { ApplicationState } from '../../store';
import { GetString } from '../StringResourceManager';
import { useEffect } from 'react';
import { LocalizationProvider, loadMessages } from '@progress/kendo-react-intl';
import { locales, LocaleInterface } from './GridLocale';
import esMessages from '../../utils/gridMessageKey.json';
import { CommandStatusCell } from './GridRowCommandStatus';
import { WeightAlertRecord } from '../../Interfaces/Alerts/AlertRecord';
import { DateTimeCell, TimeStampCell } from './AlertsGrid';
import { CustomDateFilterCell } from '../CustomDateFilterCell';
loadMessages(esMessages, 'fr-CA');

interface Props {
  alerts: WeightAlertRecord[];
}

const WeightRecordCell = (props: GridCellProps) => {
  const dataItem = props.dataItem;
  if (dataItem?.WeightRecord) {
    return <td>{dataItem.WeightRecord.join(', ')}</td>;
  }
  return <td></td>;
};

export const WeightDeltaCell = (props: GridCellProps) => {
  const dataItem = props.dataItem;
  if (dataItem?.WeightDelta) {
    return <td>{dataItem.WeightDelta}</td>;
  }
  return <td></td>;
};

const AlertDetailsGrid: React.FC<Props> = ({ alerts }): ReactElement => {
  const initialDataState: State = {
    skip: 0,
    take: 100,
    sort: [{ field: 'CreatedOn', dir: 'asc' }],
    group: [
      /*{ field: 'LaneNumber' }*/
    ],
  };

  const [dataState, setDataState] = React.useState<State>(initialDataState);

  const [currentLocale, setCurrentLocale] = React.useState<LocaleInterface>(
    locales[0]
  );

  const selectedLanguage: string = useSelector(
    (state: ApplicationState) => state.userSettingsReducer.selectedLanguage
  );

  useEffect(() => {
    if (selectedLanguage == 'English') {
      setCurrentLocale(locales[0]);
    } else if (selectedLanguage == 'French') {
      setCurrentLocale(locales[1]);
    }
  }, [selectedLanguage]);

  const dateFilterCell = (props: GridCustomFilterCellProps) => {
    return (
      <CustomDateFilterCell
        gridFilterCellProps={props}
        dataState={dataState}
        fieldName="CreatedOn"
      />
    );
  };

  const timestampFilterCell = (props: GridCustomFilterCellProps) => {
    return (
      <CustomDateFilterCell
        gridFilterCellProps={props}
        dataState={dataState}
        fieldName="Timestamp"
      />
    );
  };

  return (
    <LocalizationProvider language={currentLocale.language}>
      <Grid
        style={{ height: '53vh' }}
        sortable={true}
        filterable={true}
        groupable={true}
        reorderable={true}
        pageable={{
          buttonCount: 4,
          pageSizes: [10, 20, 50, 100, 500],
        }}
        resizable={true}
        take={100}
        data={process(alerts, dataState)}
        {...dataState}
        onDataStateChange={(e: GridDataStateChangeEvent) => {
          setDataState(e.dataState);
        }}
      >
        <GridColumn
          field="Division"
          title={GetString('COLUMN_TITLE_DIVISION')}
          width="150px"
        />
        <GridColumn
          field="SiteNumber"
          title={GetString('STORE_TITLE')}
          width="150px"
        />
        <GridColumn
          field="LaneNumber"
          title={GetString('COLUMN_TITLE_LANE')}
          width="150px"
        />
        <GridColumn
          field="NodeGroup"
          title={GetString('COLUMN_TITLE_CLUSTER')}
          width="140px"
        />
        <GridColumn
          field="OperatorID"
          title={GetString('OPERATOR')}
          width="150px"
        />
        <GridColumn
          field="CommandStatus"
          title={GetString('STATUS_TITLE')}
          width="150px"
          cells={{ data: CommandStatusCell }}
        />
        <GridColumn
          field="WeightRecord"
          title={GetString('WEIGHT_RECORD')}
          width="290px"
          filterable={false}
          cells={{ data: WeightRecordCell }}
        />
        <GridColumn
          field="WeightDelta"
          title={GetString('WEIGHT_DELTA')}
          width="185px"
          filter="numeric"
          cells={{ data: WeightDeltaCell }}
        />
        <GridColumn
          field="CreatedOn"
          title={GetString('CREATED_ON_TITLE')}
          width="220px"
          cells={{ data: DateTimeCell, filterCell: dateFilterCell }}
        />
        <GridColumn
          field="Timestamp"
          title={GetString('TIMESTAMP')}
          width="220px"
          cells={{ data: TimeStampCell, filterCell: timestampFilterCell }}
        />
      </Grid>
    </LocalizationProvider>
  );
};

export default AlertDetailsGrid;
